import { ArrowUpRight } from "@phosphor-icons/react";
import { FIRM } from "../../lib/firm";
import { trackEvent } from "../../lib/analytics";

const CASES = [
  {
    tag: "Case 01 · Married Couple, TX",
    headline: "Ages 64 and 62. Recently retired.",
    balanceLabel: "IRA / 401(k)",
    balance: "$2,800,000",
    outcomeLabel: "Projected lifetime tax savings",
    outcome: "$1,300,000",
    context:
      "RMDs projected at $140,000+ annually starting age 73.",
    strategies:
      "Roth conversion sequencing · IRMAA bracket management · SECURE Act beneficiary restructuring",
  },
  {
    tag: "Case 02 · Single Professional",
    headline: "Age 68. High W-2 income through retirement.",
    balanceLabel: "IRA / 401(k)",
    balance: "$5,600,000",
    outcomeLabel: "Projected lifetime & inheritance tax savings",
    outcome: "$4,400,000",
    context:
      "Candidate for IRA-LLC advanced conversion given balance size.",
    strategies:
      "IRA-LLC valuation discount · Multi-year Roth plan · Trust-based beneficiary restructuring",
  },
];

export default function CaseStudies() {
  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="py-24 lg:py-32 bg-cream-alt"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-overline uppercase font-semibold text-accent-gold">
                § 02
              </span>
              <span className="h-px w-10 bg-accent-gold/60" />
              <span className="text-xs tracking-overline uppercase font-semibold text-deep-teal/70">
                Client Outcomes
              </span>
            </div>
            <h2
              className="font-serif font-light text-deep-teal leading-[1.04] tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 4.8vw, 4.25rem)" }}
              data-testid="case-studies-headline"
            >
              What the opportunity{" "}
              <span className="italic text-accent-gold">
                looks like in practice.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-charcoal-teal text-base leading-relaxed">
              Illustrative examples based on real client engagements.
              Identifying details have been removed.
            </p>
          </div>
        </div>

        <div
          className="grid lg:grid-cols-2 gap-px bg-sage/30 border border-sage/30"
          data-testid="case-studies-grid"
        >
          {CASES.map((c, i) => (
            <article
              key={c.tag}
              data-testid={`case-study-${i + 1}`}
              className="bg-cream p-8 lg:p-12 flex flex-col"
            >
              <div className="text-[10px] tracking-overline uppercase text-accent-gold font-semibold">
                {c.tag}
              </div>
              <p className="mt-4 text-deep-teal font-serif text-2xl lg:text-3xl leading-snug">
                {c.headline}
              </p>

              <div className="mt-10 grid grid-cols-2 gap-8 pt-8 border-t border-sage-soft">
                <div>
                  <div className="text-[10px] tracking-overline uppercase text-charcoal-teal/60 font-semibold">
                    {c.balanceLabel}
                  </div>
                  <div className="mt-2 font-serif text-3xl lg:text-4xl text-deep-teal">
                    {c.balance}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-overline uppercase text-accent-gold font-semibold">
                    {c.outcomeLabel}
                  </div>
                  <div className="mt-2 font-serif text-3xl lg:text-4xl text-accent-gold">
                    {c.outcome}
                  </div>
                </div>
              </div>

              <p className="mt-8 text-charcoal-teal text-sm leading-relaxed">
                {c.context}
              </p>
              <p className="mt-3 text-deep-teal text-sm leading-relaxed italic">
                {c.strategies}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <p className="text-xs italic text-charcoal-teal/70 leading-relaxed max-w-3xl">
            These examples are illustrative and based on real client scenarios
            with identifying details removed. Projected savings are based on
            financial modeling at the time of engagement and depend on actual
            tax rates, investment returns, and individual circumstances. Past
            results are not a guarantee of future outcomes. This content does
            not constitute tax, legal, or investment advice.
          </p>
          <a
            href={FIRM.calendly15}
            target="_blank"
            rel="noreferrer"
            data-testid="case-studies-cta"
            onClick={() =>
              trackEvent("calendly_click_15min", {
                meta: { source: "case_studies" },
              })
            }
            className="group inline-flex items-center gap-3 px-7 py-4 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition-all whitespace-nowrap"
          >
            See if your situation qualifies
            <ArrowUpRight
              size={16}
              weight="light"
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
